import React from 'react';
import { Link } from 'react-router-dom';
import { buttonReset, color, font, space, weight } from '../theme';
import { NavState } from '../layout/CardLink';
import Icon from './Icon';

interface ListRowProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  // Before the text: an Avatar, a color marker or an icon
  leading?: React.ReactNode;
  // At the far end: a number, a badge
  trailing?: React.ReactNode;
  // A row with `to` is a real link (Ctrl-click and "open in new tab" work)
  to?: string;
  // A link to another site (documents): opens in a new tab, with an external-link icon
  href?: string;
  state?: NavState;
  onClick?: () => void;
  // Line above the row; turn it off for the first row in a card
  divider?: boolean;
}

/* One row in a list card, edge to edge, at least 56px tall. Rows that go somewhere
   get a chevron; put several in a <Card padding={0}>. */
const ListRow = ({ title, subtitle, leading, trailing, to, href, state, onClick, divider = true }: ListRowProps) => {
  const interactive = Boolean(to || href || onClick);
  const style: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: space.md,
    minHeight: 56,
    padding: `${space.sm + 2}px ${space.lg}px`,
    borderTop: divider ? `1px solid ${color.divider}` : 'none',
    color: color.text,
    textDecoration: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };
  const content = (
    <>
      {leading}
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.xxs }}>
        <span style={{ fontSize: font.body, fontWeight: weight.medium, lineHeight: 1.35 }}>{title}</span>
        {subtitle && <span style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.35 }}>{subtitle}</span>}
      </span>
      {trailing}
      {interactive && <span style={{ color: color.textTertiary }}><Icon name={href ? 'external' : 'chevronForward'} size={18} /></span>}
    </>
  );

  if (to) return <Link to={to} state={state} style={style}>{content}</Link>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" style={style}>{content}</a>;
  if (onClick) return <button type="button" onClick={onClick} style={{ ...buttonReset, ...style }}>{content}</button>;
  return <div style={style}>{content}</div>;
};

export default ListRow;
